import { useEffect, useRef, useState } from 'react';

import { BottomPanelKey } from 'types';
import { hasAvailableMinutesSelector, profileUserSelector } from 'store/selectors/profile';
import { useStore } from 'hooks';
import { AnimatedContent, BottomSheet } from 'components';

import CreateChannel from './FreeTrialPanel.CreateChannel';
import StartStreaming from './FreeTrialPanel.StartStreaming';
import UpgradePanel from './FreeTrialPanel.Upgrade';

import styles from './FreeTrialPanel.module.scss';

export type FreeTrialPanelProps = {
  show?: boolean;
  onSchedule?(): void;
  onStart?(): void;
  onClose?(): void;
};

const FreeTrialPanel = ({ show, onSchedule, onStart, onClose }: FreeTrialPanelProps) => {
  const profile = useStore(profileUserSelector);
  const hasAvailableMinutes = useStore(hasAvailableMinutesSelector);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const isFromChannelCreation = useRef(false);

  useEffect(() => {
    if (!profile?.channelName) {
      // Show Create Channel section
      setCurrentStepIndex(0);
    } else if (!hasAvailableMinutes) {
      // Show Upgrade section
      setCurrentStepIndex(2);
    } else {
      // Show Start Streaming section
      setCurrentStepIndex(1);
    }
  }, [hasAvailableMinutes, profile?.channelName, profile?.onFreeTrial]);

  const handleOnChannelCreated = () => {
    isFromChannelCreation.current = true;
  };

  const handleOnClose = () => onClose?.();

  const steps = [
    {
      name: 'CHANNEL_CREATION',
      title: 'Create channel',
      content: <CreateChannel onSuccess={handleOnChannelCreated} />
    },
    {
      name: 'START_STREAMING',
      title: 'Start streaming',
      content: (
        <StartStreaming
          message={isFromChannelCreation.current ? 'Channel is created successfully' : undefined}
          onSchedule={onSchedule}
          onStart={onStart}
        />
      )
    },
    {
      name: 'EXPIRED',
      title: 'Available minutes expired',
      content: <UpgradePanel />
    }
  ];

  const currentStep = steps[currentStepIndex];

  return (
    <BottomSheet
      id={BottomPanelKey.FREE_TRIAL}
      title={currentStep.title}
      show={show}
      unmountOnExit
      onClose={handleOnClose}
    >
      <div className={styles.container}>
        <div className={styles.animated}>
          <AnimatedContent activeContentIndex={currentStepIndex}>
            {steps.map(step => (
              <div key={step.name} className={styles.content}>
                {step.content}
              </div>
            ))}
          </AnimatedContent>
        </div>
      </div>
    </BottomSheet>
  );
};

export default FreeTrialPanel;
